import { formatDateTime, formatDayMonth, t } from "@hakili/i18n";
import { ArrowRightIcon, HakiliMark, Toast } from "@hakili/ui";
import { AlertTriangle, Copy, Info, Lock, Minimize2 } from "lucide-react";
import { Fragment, type ReactElement, useEffect, useRef, useState } from "react";

import { useNoticeTimer } from "../notices.js";
import { doorWords, type QuestionExchangeView, SUGGESTIONS } from "./question-panel.js";

/**
 * `ASK-WIDE` (D-1028-4; D2 `6a9746bf`, frames `154` to `160`, D2-design §12): **the conversation with Hakili in a wide in-page panel**, from the
 * thread the question door serves and nothing else.
 *
 * - Oldest at the top, the list scrolled to its end on open and after each answer.
 * - « VOUS » carries the date and **no name** (the door serves none). The answer is a white card of plain text, split at the blank line.
 * - « Lu dans » keeps the door's words (`doorWords`): **no pièce button** until the door serves sources.
 * - No figure is made here; « Copier » copies the last answer's text; « Épingler » and « Exporter » are dropped.
 * - Docked at 1100 px and above (no veil: the review beside it stays live), modal below it (the page behind locked).
 * - Frame `160` (the answer that failed the figure check) waits for the door to serve that state.
 */
export interface WideConversationProps {
  readonly thread: readonly QuestionExchangeView[];
  /** The question out now (frame 157), or `null`. */
  readonly asking: string | null;
  /** The product's sentence a refused question got (frame 158), with the question it answered. */
  readonly refusal: string | null;
  readonly asked: string | null;
  /** Under 1100 px: modal, the page behind locked. */
  readonly modal: boolean;
  /** « {name} · {ref} », from the served file; `null` where the file names no client. */
  readonly subtitle: string | null;
  /** The strip (contract part 1): the pièces the dossier has received, from served data, and whether an expertise report stands. */
  readonly documents: readonly { readonly code: string; readonly label: string }[];
  readonly report: boolean;
  /** A viewer stands over the panel: Échap is the viewer's. */
  readonly suspended: boolean;
  readonly onDocument: (code: string, label: string) => void;
  readonly onReport: () => void;
  readonly onAsk: (question: string) => void;
  readonly onClose: () => void;
}

const CHIPS: readonly string[] = [...SUGGESTIONS, t("bank.conversation.puce2"), t("bank.conversation.puce3")];

/** `ASK-ONE-PATTERN` (D2 `screens2.js:280`): the day an exchange was asked, « 26 octobre »; empty where the date is unreadable. */
const dayOf = (iso: string): string => {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? "" : formatDayMonth(at);
};

const stamp = (iso: string): string => {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? "" : formatDateTime(at);
};

export function WideConversation(props: WideConversationProps): ReactElement {
  const [question, setQuestion] = useState("");
  const [copied, setCopied] = useState(false);
  const [reason, setReason] = useState<"vide" | "attente" | null>(null);
  const body = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const waiting = props.asking !== null;
  /** How an exchange ended: the served `state`, or the old door's `failure` sentence. */
  const kindOf = (exchange: QuestionExchangeView): "answer" | "unverified" | "refused" | "failed" =>
    exchange.failure !== null || exchange.state === "failed"
      ? "failed"
      : exchange.state === "unverified" || exchange.state === "refused"
        ? exchange.state
        : "answer";
  const last = props.thread.length === 0 ? null : props.thread[props.thread.length - 1];
  const manyDays =
    new Set(props.thread.map((exchange) => dayOf(exchange.askedAt)).filter((day) => day !== "")).size > 1;

  // Focus goes into the field on open.
  useEffect(() => {
    field.current?.focus();
  }, []);

  // Échap closes, wherever the focus is inside the page.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !props.suspended) props.onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [props]);

  // Modal: the page behind does not scroll.
  useEffect(() => {
    if (!props.modal) return;
    const before = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = before;
    };
  }, [props.modal]);

  // The list stands at its end on open and after each exchange.
  useEffect(() => {
    const element = body.current;
    if (element !== null) element.scrollTop = element.scrollHeight;
  }, [props.thread, props.asking, props.refusal]);

  // D2 `314d2bf7`: « Réponse copiée. », said once the clipboard took the text, and gone as every bank toast goes (`NOTICE_MS`).
  useNoticeTimer(copied ? "copiee" : null, () => {
    setCopied(false);
  });

  const send = (): void => {
    const said = question.trim();
    if (waiting) {
      // D2 frame 157: the field stays white and the send solid while Hakili reads; a press says why, in the lock chip (no silent fade).
      setReason("attente");
      return;
    }
    if (said === "") {
      // D2's send is always solid; an empty press asks nothing and says why, in the lock chip.
      setReason("vide");
      return;
    }
    props.onAsk(said);
    setQuestion("");
  };

  const noExchange = props.thread.length === 0 && !waiting && props.refusal === null;

  return (
    <aside
      data-wide-panel=""
      data-mode={props.modal ? "modal" : "docked"}
      role="dialog"
      aria-modal={props.modal ? "true" : "false"}
      aria-label={t("bank.conversation.titre")}
      data-testid="bank-conversation"
    >
      <header data-wide="tete">
        <div>
          <h2>{t("bank.conversation.titre")}</h2>
          {props.subtitle === null ? null : (
            <p data-wide="sous-titre" data-testid="bank-conversation-sous-titre">
              {props.subtitle}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={props.onClose}
          data-wide="reduire"
          data-testid="bank-conversation-reduire"
        >
          <Minimize2 width={18} height={18} aria-hidden="true" />
          <span>{t("bank.conversation.reduire")}</span>
        </button>
      </header>
      {props.documents.length === 0 && !props.report ? null : (
        <div
          data-wide="pieces"
          role="group"
          aria-label={t("bank.conversation.pieces")}
          data-testid="bank-conversation-pieces"
        >
          <span data-wide="pieces-titre" data-testid="bank-conversation-pieces-titre">
            {t("bank.conversation.pieces")}
          </span>
          {props.documents.map((piece) => (
            <button
              key={piece.code}
              type="button"
              onClick={() => {
                props.onDocument(piece.code, piece.label);
              }}
              data-testid={`bank-conversation-piece-${piece.code}`}
            >
              {piece.label}
            </button>
          ))}
          {props.report ? (
            <button type="button" onClick={props.onReport} data-testid="bank-conversation-rapport">
              {t("bank.conversation.rapport")}
            </button>
          ) : null}
        </div>
      )}
      <div data-wide="corps" ref={body} tabIndex={-1} data-testid="bank-conversation-corps">
        {noExchange ? (
          <div data-wide="vide" data-testid="bank-conversation-vide">
            <span data-wide="marque" aria-hidden="true">
              <HakiliMark size={56} />
            </span>
            <h3>{t("bank.conversation.videTitre")}</h3>
            <p>{t("bank.conversation.videTexte")}</p>
          </div>
        ) : (
          <ol data-wide="liste" data-testid="bank-conversation-liste">
            {props.thread.map((exchange, at) => {
              const key = String(exchange.position);
              const day = dayOf(exchange.askedAt);
              const previous = at === 0 ? null : props.thread[at - 1];
              // D2 `withDay`: a line before each day, drawn only when the history spans more than one day.
              const dayLine =
                manyDays &&
                day !== "" &&
                (previous === null || previous === undefined || dayOf(previous.askedAt) !== day);
              return (
                <Fragment key={key}>
                  {dayLine ? (
                    <li data-wide="jour" data-testid="bank-conversation-jour">
                      <span>{day}</span>
                    </li>
                  ) : null}
                  <li data-testid={`bank-conversation-echange-${key}`}>
                    <div data-wide="vous" data-testid={`bank-conversation-vous-${key}`}>
                      <span data-wide="qui">{t("bank.conversation.vous")}</span>
                      {exchange.askedAt === "" || kindOf(exchange) === "refused" ? null : (
                        <time dateTime={exchange.askedAt}>{stamp(exchange.askedAt)}</time>
                      )}
                      <p data-testid={`bank-conversation-question-${key}`}>{exchange.question}</p>
                    </div>
                    <div data-wide="hakili">
                      <span data-wide="qui">
                        <span data-wide="marque" aria-hidden="true">
                          <HakiliMark size={18} />
                        </span>
                        {t("bank.conversation.hakili")}
                      </span>
                      {kindOf(exchange) === "unverified" ? (
                        <p data-wide="non-verifiee" data-testid={`bank-conversation-non-verifiee-${key}`}>
                          <Info width={18} height={18} aria-hidden="true" />
                          <span>{t("bank.conversation.nonVerifiee")}</span>
                        </p>
                      ) : kindOf(exchange) === "refused" ? (
                        <div data-wide="reponse" data-testid={`bank-conversation-reponse-${key}`}>
                          <p>{exchange.answer ?? ""}</p>
                        </div>
                      ) : kindOf(exchange) === "answer" ? (
                        <>
                          <div data-wide="reponse" data-testid={`bank-conversation-reponse-${key}`}>
                            {(exchange.answer ?? "").split(/\n\s*\n/u).map((paragraph, at) => (
                              <p key={String(at)}>{paragraph}</p>
                            ))}
                          </div>
                          {exchange.doorsRead.length === 0 ? null : (
                            <p data-wide="portes" data-testid={`bank-conversation-portes-${key}`}>
                              {t("bank.dossier.questionPortes")} {doorWords(exchange.doorsRead)}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <p data-wide="echec" role="alert" data-testid={`bank-conversation-echec-${key}`}>
                            <AlertTriangle width={18} height={18} aria-hidden="true" />
                            <span>{t("bank.conversation.echec")}</span>
                          </p>
                          {exchange === last ? (
                            <div data-wide="reessai">
                              <button
                                type="button"
                                data-wide="reessayer"
                                onClick={() => {
                                  if (waiting) {
                                    setReason("attente");
                                    return;
                                  }
                                  props.onAsk(exchange.question);
                                }}
                                data-testid={`bank-conversation-reessayer-${key}`}
                              >
                                {t("bank.conversation.reessayer")}
                              </button>
                            </div>
                          ) : null}
                        </>
                      )}
                      {kindOf(exchange) !== "failed" && kindOf(exchange) !== "refused" ? (
                        <p data-wide="note">{t("bank.dossier.questionDecide")}</p>
                      ) : null}
                      {exchange === last && kindOf(exchange) === "answer" && exchange.answer !== null ? (
                        <button
                          type="button"
                          data-wide="copier"
                          onClick={() => {
                            void navigator.clipboard.writeText(exchange.answer ?? "").then(
                              () => {
                                setCopied(true);
                              },
                              () => undefined,
                            );
                          }}
                          data-testid={`bank-conversation-copier-${key}`}
                        >
                          <Copy width={14} height={14} aria-hidden="true" />
                          {t("bank.conversation.copier")}
                        </button>
                      ) : null}
                    </div>
                  </li>
                </Fragment>
              );
            })}
            {props.asking === null ? null : (
              <li data-testid="bank-conversation-en-attente">
                <div data-wide="vous">
                  <span data-wide="qui">{t("bank.conversation.vous")}</span>
                  <p data-testid="bank-conversation-attente-question">{props.asking}</p>
                </div>
                <div
                  data-wide="hakili"
                  role="status"
                  aria-live="polite"
                  data-testid="bank-conversation-attente"
                >
                  <span data-wide="qui">
                    <span data-wide="marque" aria-hidden="true">
                      <HakiliMark size={18} />
                    </span>
                    {t("bank.conversation.hakili")}
                  </span>
                  <p data-wide="attente">{t("bank.conversation.attente")}</p>
                  <div data-wide="squelette" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </div>
                </div>
              </li>
            )}
            {props.refusal === null || waiting ? null : (
              <li data-testid="bank-conversation-refuse">
                {props.asked === null ? null : (
                  <div data-wide="vous">
                    <span data-wide="qui">{t("bank.conversation.vous")}</span>
                    <p data-testid="bank-conversation-refus-question">{props.asked}</p>
                  </div>
                )}
                <div data-wide="hakili">
                  <span data-wide="qui">
                    <span data-wide="marque" aria-hidden="true">
                      <HakiliMark size={18} />
                    </span>
                    {t("bank.conversation.hakili")}
                  </span>
                  <div data-wide="reponse" role="alert" data-testid="bank-conversation-refus">
                    {props.refusal}
                  </div>
                </div>
              </li>
            )}
          </ol>
        )}
      </div>
      {copied ? (
        <div data-wide="toast" data-testid="bank-conversation-copiee">
          <Toast>{t("bank.conversation.copiee")}</Toast>
        </div>
      ) : null}
      <footer data-wide="pied">
        <div data-wide="puces">
          {CHIPS.map((chip, at) => (
            <button
              key={chip}
              type="button"
              onClick={() => {
                if (waiting) {
                  setReason("attente");
                  return;
                }
                props.onAsk(chip);
              }}
              data-testid={`bank-conversation-puce-${String(at + 1)}`}
            >
              {chip}
            </button>
          ))}
        </div>
        <form
          data-wide="champ"
          data-testid="bank-conversation-formulaire"
          onSubmit={(event) => {
            event.preventDefault();
            send();
          }}
        >
          <input
            ref={field}
            type="text"
            value={question}
            onChange={(event) => {
              setQuestion(event.target.value);
              setReason(null);
            }}
            placeholder={t("bank.bay.questionChamp")}
            aria-label={t("bank.conversation.champ")}
            autoComplete="off"
            data-testid="bank-conversation-champ"
          />
          <button
            type="submit"
            aria-label={t("bank.dossier.questionEnvoyer")}
            title={t("bank.dossier.questionEnvoyer")}
            data-testid="bank-conversation-envoyer"
          >
            <ArrowRightIcon />
          </button>
        </form>
        {reason === null ? null : (
          <p data-wide="raison" role="status" data-testid="bank-conversation-question-vide">
            <Lock width={15} height={15} aria-hidden="true" />
            {reason === "attente"
              ? t("bank.conversation.questionEnCours")
              : t("bank.conversation.questionVide")}
          </p>
        )}
        <p data-wide="aide" data-testid="bank-conversation-aide">
          {t("bank.conversation.aide")}
        </p>
      </footer>
    </aside>
  );
}
